import React, { useEffect, useState } from 'react';
import '../css/accounting.css';

const currentMonthKey = () => new Date().toISOString().slice(0, 7);

const createAccountRow = (seed = {}) => ({
  key: `account-${Date.now()}-${Math.random()}`,
  name: seed.name || '',
  account_type: seed.account_type || 'savings',
  balance: seed.balance ?? 0
});

const createCreditRow = () => ({
  key: `credit-${Date.now()}-${Math.random()}`,
  name: '',
  credit_limit: ''
});

const createExpenseRow = () => ({
  key: `expense-${Date.now()}-${Math.random()}`,
  name: '',
  amount: '',
  category: '',
  recurring: true,
  month: currentMonthKey()
});

function Onboarding({ onComplete }) {
  const [monthlyIncome, setMonthlyIncome] = useState('');
  const [monthlyBonus, setMonthlyBonus] = useState('');
  const [onboardingMonth, setOnboardingMonth] = useState(currentMonthKey());
  const [accounts, setAccounts] = useState([]);
  const [credits, setCredits] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  const user = localStorage.getItem('loggedInUsername');

  useEffect(() => {
    const loadSetupStatus = async () => {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch('/api/accounting/setup/status', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ user })
        });

        if (!response.ok) {
          throw new Error('Failed to load setup status');
        }

        const data = await response.json();

        if (!data.needs_onboarding) {
          if (onComplete) {
            onComplete(data.onboarding_month || '');
          }
          return;
        }

        const accountRows = (Array.isArray(data.accounts) ? data.accounts : []).map((row) => createAccountRow({
          name: row.name,
          account_type: row.account_type,
          balance: row.balance || 0
        }));

        setAccounts(accountRows.length > 0 ? accountRows : [createAccountRow({ name: 'Current account', account_type: 'current', balance: 0 })]);
        setOnboardingMonth(data.onboarding_month || currentMonthKey());
      } catch (error) {
        setStatusMessage('Unable to load onboarding details. Please refresh and try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadSetupStatus();
  }, [onComplete, user]);

  const updateAccount = (key, field, value) => {
    setAccounts((prev) => prev.map((item) => (item.key === key ? { ...item, [field]: value } : item)));
  };

  const updateCredit = (key, field, value) => {
    setCredits((prev) => prev.map((item) => (item.key === key ? { ...item, [field]: value } : item)));
  };

  const updateExpense = (key, field, value) => {
    setExpenses((prev) => prev.map((item) => (item.key === key ? { ...item, [field]: value } : item)));
  };

  const handleSubmit = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }

    const cleanedAccounts = accounts
      .map((item) => ({
        name: String(item.name || '').trim(),
        account_type: item.account_type || 'savings',
        balance: Number(item.balance || 0)
      }))
      .filter((item) => item.name);

    if (monthlyIncome === '') {
      setStatusMessage('Monthly income is required.');
      return;
    }

    if (cleanedAccounts.length === 0) {
      setStatusMessage('At least one account is required.');
      return;
    }

    const cleanedCredits = credits
      .map((item) => ({
        name: String(item.name || '').trim(),
        credit_limit: Number(item.credit_limit || 0)
      }))
      .filter((item) => item.name);

    const cleanedExpenses = expenses
      .map((item) => ({
        name: String(item.name || '').trim(),
        amount: Number(item.amount || 0),
        category: String(item.category || '').trim(),
        recurring: Boolean(item.recurring),
        month: item.recurring ? '' : item.month
      }))
      .filter((item) => item.name);

    try {
      setIsSaving(true);
      setStatusMessage('');

      const response = await fetch('/api/accounting/setup/complete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          user,
          onboarding_month: onboardingMonth || currentMonthKey(),
          income: {
            monthly_income: Number(monthlyIncome || 0),
            monthly_bonus: Number(monthlyBonus || 0)
          },
          accounts: cleanedAccounts,
          credits: cleanedCredits,
          expenses: cleanedExpenses
        })
      });

      const data = await response.json();
      if (!response.ok) {
        setStatusMessage(data?.error || 'Unable to save onboarding details.');
        return;
      }

      if (onComplete) {
        onComplete(data.onboarding_month || onboardingMonth || currentMonthKey());
      }
    } catch (error) {
      setStatusMessage('Unable to save onboarding details.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="lib-page">
        <section className="accounting-page">
          <div className="loading-state">Loading onboarding...</div>
        </section>
      </div>
    );
  }

  return (
    <div className="lib-page">
      <section className="accounting-page">
        <div className="section-header">
          <div>
            <h2>Welcome to Manderley Accounting</h2>
            <p className="section-subtitle">Set up your baseline so forecasts can start from your onboarding month.</p>
          </div>
          <label>
            Onboarding month
            <input type="month" value={onboardingMonth} onChange={(event) => setOnboardingMonth(event.target.value)} />
          </label>
        </div>

        <section className="panel">
          <h3>Income (required)</h3>
          <div className="form-grid">
            <div className="form-row">
              <label>
                Monthly income
                <input type="number" min="0" step="0.01" value={monthlyIncome} onChange={(event) => setMonthlyIncome(event.target.value)} />
              </label>
              <label>
                Monthly bonus
                <input type="number" min="0" step="0.01" value={monthlyBonus} onChange={(event) => setMonthlyBonus(event.target.value)} />
              </label>
            </div>
          </div>
        </section>

        <section className="panel">
          <h3>Accounts (required)</h3>
          <div className="form-grid">
            {accounts.map((account) => (
              <div key={account.key} className="form-row">
                <label>
                  Name
                  <input value={account.name} onChange={(event) => updateAccount(account.key, 'name', event.target.value)} />
                </label>
                <label>
                  Type
                  <select value={account.account_type} onChange={(event) => updateAccount(account.key, 'account_type', event.target.value)}>
                    <option value="current">Current</option>
                    <option value="savings">Savings</option>
                    <option value="investment">Investment</option>
                  </select>
                </label>
                <label>
                  Starting balance
                  <input
                    type="number"
                    step="0.01"
                    value={account.balance}
                    onChange={(event) => updateAccount(account.key, 'balance', event.target.value)}
                  />
                </label>
                <button className="login-button" type="button" onClick={() => setAccounts((prev) => prev.filter((item) => item.key !== account.key))}>
                  Remove
                </button>
              </div>
            ))}
            <div className="form-row">
              <button className="login-button" type="button" onClick={() => setAccounts((prev) => [...prev, createAccountRow()])}>Add account</button>
            </div>
          </div>
        </section>

        <section className="panel">
          <h3>Credit lines (optional)</h3>
          <div className="form-grid">
            {credits.map((credit) => (
              <div key={credit.key} className="form-row">
                <label>
                  Name
                  <input value={credit.name} onChange={(event) => updateCredit(credit.key, 'name', event.target.value)} />
                </label>
                <label>
                  Credit limit
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={credit.credit_limit}
                    onChange={(event) => updateCredit(credit.key, 'credit_limit', event.target.value)}
                  />
                </label>
                <button className="login-button" type="button" onClick={() => setCredits((prev) => prev.filter((item) => item.key !== credit.key))}>
                  Remove
                </button>
              </div>
            ))}
            <div className="form-row">
              <button className="login-button" type="button" onClick={() => setCredits((prev) => [...prev, createCreditRow()])}>Add credit line</button>
            </div>
          </div>
        </section>

        <section className="panel">
          <h3>Expenses (optional)</h3>
          <div className="form-grid">
            {expenses.map((expense) => (
              <div key={expense.key} className="form-row">
                <label>
                  Name
                  <input value={expense.name} onChange={(event) => updateExpense(expense.key, 'name', event.target.value)} />
                </label>
                <label>
                  Amount
                  <input type="number" min="0" step="0.01" value={expense.amount} onChange={(event) => updateExpense(expense.key, 'amount', event.target.value)} />
                </label>
                <label>
                  Category
                  <input value={expense.category} onChange={(event) => updateExpense(expense.key, 'category', event.target.value)} />
                </label>
                <label>
                  Month
                  <input
                    type="month"
                    value={expense.month}
                    onChange={(event) => updateExpense(expense.key, 'month', event.target.value)}
                    disabled={expense.recurring}
                  />
                </label>
                <label className="inline-switch">
                  <input
                    type="checkbox"
                    checked={expense.recurring}
                    onChange={(event) => updateExpense(expense.key, 'recurring', event.target.checked)}
                  />
                  Recurring
                </label>
                <button className="login-button" type="button" onClick={() => setExpenses((prev) => prev.filter((item) => item.key !== expense.key))}>
                  Remove
                </button>
              </div>
            ))}
            <div className="form-row">
              <button className="login-button" type="button" onClick={() => setExpenses((prev) => [...prev, createExpenseRow()])}>Add expense</button>
            </div>
          </div>
        </section>

        <section className="panel">
          <div className="form-row">
            <button className="login-button" type="button" onClick={handleSubmit} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Complete setup'}
            </button>
          </div>
          {statusMessage && <span className="section-subtitle">{statusMessage}</span>}
        </section>
      </section>
    </div>
  );
}

export default Onboarding;
